/**
 * AlternativesLink — 모듈 상세 '대안과 비교' 진입점 (queue #667 §UAT-2026-07-09-R667
 * §R667-3 · §R665 `ConfigSection`·§R666 `TroubleshootingSection` 계보).
 *
 * `ModuleDetailBody` 에 삽입되는 노드 상세 공유 섹션. 데이터는 `data/decision-guides.ts`
 * 를 `decisionGuideForCode(node.code, DECISION_GUIDES)` 로 조회한다(BE 0 · 정적 import).
 * 0건이면 섹션 전체를 숨긴다(빈 카드/EmptyState 미노출 — §R665/§R666 설계 결정 답습).
 * 클릭 시 전역 선택 가이드 뷰의 해당 군 비교표로 딥링크(`/showcase?view=decision&guide=
 * <id>&highlight=<code>` · 현재 코드 행 하이라이트).
 */
import { useTranslation } from 'react-i18next';
import { StatusPill } from '@ui-kit';
import type { ModuleNode } from '../types/showcase';
import { DECISION_GUIDES, decisionGuideForCode, guideOptionCodes, recommendedOption } from '../data/decision-guides';

const cardStyle: React.CSSProperties = {
  textAlign: 'left',
  width: '100%',
  padding: 'var(--spacing-3)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  background: 'var(--color-surface-elevated)',
  cursor: 'pointer',
  fontFamily: 'var(--font-family-sans)',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-1)',
  marginBottom: 'var(--spacing-2)',
};

const captionStyle: React.CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-muted)',
  margin: 0,
};

interface Props {
  node: ModuleNode;
  /** 대안 카드 클릭 — 전역 선택 가이드 뷰로 전환 + 해당 군/코드 하이라이트 */
  onNavigate?: (guideId: string, code: string) => void;
}

export function AlternativesLink({ node, onNavigate }: Props) {
  const { t } = useTranslation();
  const guides = decisionGuideForCode(node.code, DECISION_GUIDES);

  // §R667-3 empty/error — 0건(또는 잘못된 shape 방어로 빈 배열)이면 섹션 전체 숨김.
  if (!guides || guides.length === 0) return null;

  return (
    <section aria-labelledby="alternatives-link-title" style={{ marginBottom: 'var(--spacing-4)' }}>
      <h3
        id="alternatives-link-title"
        style={{
          fontSize: 'var(--font-size-lg)',
          fontWeight: 700,
          color: 'var(--color-text-primary)',
          margin: '0 0 var(--spacing-1)',
        }}
      >
        {t('showcase.decision.alt.title')}
      </h3>
      <p style={{ ...captionStyle, marginBottom: 'var(--spacing-3)' }}>{t('showcase.decision.alt.hint')}</p>

      {guides.map((guide) => {
        const recommended = recommendedOption(guide);
        return (
          <button
            key={guide.id}
            type="button"
            data-testid={`alternatives-link-${guide.id}`}
            onClick={() => onNavigate?.(guide.id, node.code)}
            style={cardStyle}
          >
            <p style={captionStyle}>{t('showcase.decision.alt.caption', { code: node.code, guide: t(guide.titleKey) })}</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', flexWrap: 'wrap' }}>
              {guideOptionCodes(guide).map((code) => (
                <code
                  key={code}
                  style={{
                    fontFamily: 'var(--font-family-mono)',
                    fontSize: 'var(--font-size-xs)',
                    background: code === node.code ? 'var(--color-surface-muted)' : 'var(--color-surface-background)',
                    border: '1px solid var(--color-surface-border)',
                    padding: '2px var(--spacing-2)',
                    borderRadius: 'var(--radius-sm)',
                    color: 'var(--color-brand-primary)',
                    fontWeight: code === node.code ? 700 : 400,
                  }}
                >
                  {code}
                </code>
              ))}
              <StatusPill tone="success">
                {t('showcase.decision.recommended')}: {recommended.code}
              </StatusPill>
            </div>
          </button>
        );
      })}
    </section>
  );
}
